import {DestroyRef, effect, inject, Injectable, signal, WritableSignal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  CustomerUserResponse,
  ProjectUserControllerService,
  ProjectUserDetailResponse,
} from '@frontend/shared-api';
import {UserStateService} from './user-state.service';

@Injectable({
  providedIn: 'root',
})
export class ProjectUserDetailService {
  private readonly projectUserController = inject(ProjectUserControllerService);
  private readonly userStateService = inject(UserStateService);

  private readonly currentUser = this.userStateService.currentUser;
  private readonly userDetailCache = signal<Map<number, ProjectUserDetailResponse | undefined>>(
    new Map(),
  );

  createUserDetailSignal(
    projectIdSignal: () => number | undefined,
    destroyRef: DestroyRef,
  ): () => ProjectUserDetailResponse | undefined {
    const userDetail = signal<ProjectUserDetailResponse | undefined>(undefined);

    effect(() => {
      const projectId = projectIdSignal();
      const user = this.currentUser();

      if (!projectId || !user || !this.userStateService.isCustomerUser()) {
        userDetail.set(undefined);
        return;
      }

      const cached = this.userDetailCache().get(projectId);
      if (cached !== undefined) {
        userDetail.set(cached);
        return;
      }

      if (this.userStateService.isCustomerUserType(user)) {
        this.loadAndCache(projectId, user, destroyRef, userDetail);
      } else {
        userDetail.set(undefined);
      }
    });

    return userDetail.asReadonly();
  }

  invalidateProjectUserDetail(projectId: number): void {
    this.userDetailCache.update((cache) => {
      const newCache = new Map(cache);
      newCache.delete(projectId);
      return newCache;
    });
  }

  private loadAndCache(
    projectId: number,
    user: CustomerUserResponse,
    destroyRef: DestroyRef,
    userDetail: WritableSignal<ProjectUserDetailResponse | undefined>,
  ) {
    this.projectUserController
      .getProjectUserWithDetails({
        projectId,
        userId: user.id,
      })
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe({
        next: (detail) => {
          this.userDetailCache.update((cache) => {
            const newCache = new Map(cache);
            newCache.set(projectId, detail);
            return newCache;
          });
          userDetail.set(detail);
        },
        error: () => {
          this.userDetailCache.update((cache) => {
            const newCache = new Map(cache);
            newCache.set(projectId, undefined);
            return newCache;
          });
          userDetail.set(undefined);
        },
      });
  }
}
